<template>
  <!-- Main Content -->
  <div>
    <!-- Breadcrumb -->
    <div class="page-header">
      <div class="page-block">
        <div class="row align-items-center">
          <div class="col-md-12">
            <div class="page-header-title">
              <h5>{{ $t('purchaseOrder.title') || 'Sales Order' }}</h5>
            </div>
            <ul class="breadcrumb">
              <li class="breadcrumb-item">
                <router-link to="/">{{ $t('common.navigation.home') }}</router-link>
              </li>
              <li class="breadcrumb-item active">{{ $t('purchaseOrder.listTitle') || 'List' }}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <ul class="nav nav-tabs invoice-tab border-bottom-0" id="myTab" role="tablist">
                <li class="nav-item" role="presentation">
                  <button
                    class="nav-link"
                    :class="{ 'active': activeTab === 'all' }"
                    @click="setActiveTab('all')"
                    type="button"
                    role="tab"
                  >
                    <span class="d-flex align-items-center gap-2">
                      {{ $t('purchaseOrder.tabs.all') || 'All' }} 
                      <span class="po-status-badge rounded-circle bg-light-primary">{{ totalStatusCount }}</span>
                    </span>
                  </button>
                </li>
                <li v-for="s in poVesprocStatuses" :key="s.id" class="nav-item" role="presentation">
                  <button
                    class="nav-link"
                    :class="{ 'active': activeTab === s.id }"
                    @click="setActiveTab(s.id)"
                    type="button"
                    role="tab"
                  >
                    <span class="d-flex align-items-center gap-2">
                      {{ s.name }}
                      <span 
                        class="po-status-badge rounded-circle"
                        :style="{ backgroundColor: s.statusColorCode || '#e9ecef', color: getContrastTextColor(s.statusColorCode || '#e9ecef') }"
                      >
                        {{ statusCounts[s.id] || 0 }}
                      </span>
                    </span>
                  </button>
                </li>
              </ul>
            </div>
            
            <div class="tab-content" id="myTabContent">
              <!-- All Purchase Orders Tab -->
              <div 
                class="tab-pane fade" 
                :class="{ 'show active': activeTab === 'all' }"
                role="tabpanel"
                tabindex="0"
              >
                <DataGrid
                  :data="allPurchaseOrders"
                  :columns="columns"
                  v-model:currentPage="currentPage"
                  v-model:pageSize="pageSize"
                  v-model:searchText="searchText"
                  :loading="isLoading"
                  :error="error"
                  :empty-state-message="$t('purchaseOrder.errors.itemsFetchFailed') || 'No sales orders found.'"
                  :search-placeholder="$t('purchaseOrder.placeholders.search') || 'Search Sales Order...'"
                  :total-items="totalItems"
                  :storage-key="'purchaseOrderList_all_columnWidths'"
                  :show-refresh="true"
                  @search="handleSearch"
                  @pageSizeChange="handlePageSizeChange"
                  @pageChange="handlePageChange"
                  @refresh="fetchPurchaseOrders"
                  @view="viewPurchaseOrder"
                  @edit="editPurchaseOrder"
                  @delete="deletePurchaseOrder"
                  :renderActions="getRenderActions"
                />
              </div>

              <!-- Status-specific tabs -->
              <div 
                v-for="status in poVesprocStatuses" 
                :key="status.id"
                class="tab-pane fade" 
                :class="{ 'show active': activeTab === status.id }"
                role="tabpanel"
                tabindex="0"
              >
                <DataGrid
                  :data="getFilteredOrdersByStatus(status.id)"
                  :columns="columns"
                  v-model:currentPage="currentPage"
                  v-model:pageSize="pageSize"
                  v-model:searchText="searchText"
                  :loading="isLoading"
                  :error="error"
                  :empty-state-message="`No ${status.name} Sales Order found.`"
                  :search-placeholder="$t('purchaseOrder.placeholders.search') || 'Search Sales Order...'"
                  :total-items="totalItems"
                  :storage-key="`purchaseOrderList_${status.id}_columnWidths`"
                  :show-refresh="true"
                  @search="handleSearch"
                  @pageSizeChange="handlePageSizeChange"
                  @pageChange="handlePageChange"
                  @refresh="fetchPurchaseOrders"
                  @view="viewPurchaseOrder"
                  @edit="editPurchaseOrder"
                  @delete="deletePurchaseOrder"
                  :renderActions="getRenderActions"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import { useDateFormatter } from '@/composables/useDateFormatter'
import { useNumberFormatter } from '@/composables/useNumberFormatter'
import { usePurchaseOrderPermissions } from '@/composables/usePurchaseOrderPermissions'
import purchaseOrderService from '@/services/purchaseOrderService'
import appStatusService from '@/services/appStatusService'
import TrackingStatus from '@/constants/trackingStatus'
import Swal from 'sweetalert2'
import DataGrid from '@/components/DataGrid.vue'

// Composables
const router = useRouter()
const store = useStore()
const { t } = useI18n()
const { formatDate, formatTimeAgo } = useDateFormatter()
const { formatCurrency } = useNumberFormatter()
const { canEditPurchaseOrders, canDeletePurchaseOrders } = usePurchaseOrderPermissions()

// Reactive data
const isLoading = ref(false)
const error = ref(null)
const purchaseOrders = ref([])
const activeTab = ref('all')
const poVesprocStatuses = ref([])
const currentPage = ref(1)
const pageSize = ref(10)
const totalItems = ref(0)
const searchText = ref('')
const statusCounts = ref({})

// Computed: Total count for "All" tab (sum of all status counts)
const totalStatusCount = computed(() => {
  let total = 0
  for (const key in statusCounts.value) {
    if (Object.hasOwn(statusCounts.value, key)) {
      total += statusCounts.value[key] || 0
    }
  }
  return total
})

// Define columns for DataGrid (aligned with RFQ list ordering)
const columns = [
  {
    key: 'buyer',
    label: t('purchaseOrder.table.buyer') || 'Buyer',
    width: '18%',
    type: 'truncate',
    sortable: true,
    sortKey: 'buyerTenantName'
  },
  { 
    key: 'poNumber', 
    label: t('purchaseOrder.table.poNumber') || ' Sales Number', 
    width: '22%',
    type: 'truncate'
  },
  { 
    key: 'date', 
    label: t('purchaseOrder.table.date') || 'Date', 
    width: '10%'
  },
  { 
    key: 'created', 
    label: t('purchaseOrder.table.created') || 'Created', 
    width: '12%'
  },
  { 
    key: 'total', 
    label: t('purchaseOrder.table.total') || 'Total', 
    width: '10%'
  },
  {
    key: 'itemCount',
    label: 'Items',
    width: '8%',
    type: 'truncate'
  },
  { 
    key: 'status', 
    label: t('purchaseOrder.table.status') || 'Status', 
    width: '10%',
    type: 'badge',
    badgeMap: {}
  }
]

// Transform purchase orders for DataGrid
const transformPurchaseOrders = (orders) => {
  return orders.map(order => ({
    id: order.id,
    buyer: order.buyerTenantName || '-',
    poNumber: order.purchaseOrderNo || '-',
    poNumberSubtitle: order.title || order.description || '',
    supplier: order.clientTenantName || '-',
    supplierSubtitle: order.supplierCode || '',
    date: formatDate(order.purchaseOrderDate),
    created: order.creationTime ? formatTimeAgo(order.creationTime) : '-',
    total: formatCurrencyWithFallback(order.grandPrice, order.currencyCode),
    itemCount: order.numberOfItems ?? order.itemCount ?? 0,
    status: order.vesprocStatusName || getStatusText(order.trackingStatusDisplay) || '-',
    statusColor: order.vesprocStatusColor,
    trackingStatus: order.trackingStatus,
    trackingStatusDisplay: order.trackingStatusDisplay,
    orderStatus: order.status,
    originalOrder: order // Keep reference to original order for actions
  }))
}

const allPurchaseOrders = computed(() => {
  return transformPurchaseOrders(purchaseOrders.value)
})

const getFilteredOrdersByStatus = (statusId) => {
  const filtered = purchaseOrders.value.filter(order => order?.vesprocStatusId === statusId)
  return transformPurchaseOrders(filtered)
}

// Methods
const setActiveTab = (tab) => {
  activeTab.value = tab
  currentPage.value = 1
  // fetchPurchaseOrders() is called by watch(activeTab)
}

const handleSearch = () => {
  currentPage.value = 1
  fetchPurchaseOrders()
}

const handlePageSizeChange = () => {
  currentPage.value = 1
  fetchPurchaseOrders()
}

const handlePageChange = () => {
  fetchPurchaseOrders()
}

const getStatusBadgeClass = (trackingStatus) => {
  if (!trackingStatus && trackingStatus !== 0) {
    return 'bg-secondary'
  }
  
  switch (trackingStatus) {
    case TrackingStatus.New:
    case TrackingStatus.ReNew:
      return 'bg-primary'
    case TrackingStatus.Processing:
    case TrackingStatus.Enqueued:
      return 'bg-warning'
    case TrackingStatus.ProcessedSuccessfully:
    case TrackingStatus.ReadyToSendToNozzle:
      return 'bg-success'
    case TrackingStatus.ProcessedWithError:
    case TrackingStatus.RevisionRequested:
      return 'bg-danger'
    case TrackingStatus.Declined:
    case TrackingStatus.DeclinedSuccessfully:
    case TrackingStatus.DeletedFromNozzle:
      return 'bg-secondary'
    default:
      return 'bg-secondary'
  }
}

const getStatusText = (trackingStatusDisplay) => {
  return trackingStatusDisplay || 'Unknown'
}

// Compute readable text color (black/white) based on background color
const getContrastTextColor = (bg) => {
  if (!bg) return '#212529'
  let hex = bg.trim()
  if (hex.startsWith('rgb')) {
    try {
      const m = hex.match(/rgba?\((\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i)
      if (m) {
        const r = parseInt(m[1], 10), g = parseInt(m[2], 10), b = parseInt(m[3], 10)
        const luminance = (0.299 * r + 0.587 * g + 0.114 * b)
        return luminance > 186 ? '#212529' : '#fff'
      }
    } catch {}
  }
  if (hex[0] === '#') hex = hex.slice(1)
  if (hex.length === 3) {
    hex = hex.split('').map(c => c + c).join('')
  }
  const r = parseInt(hex.substring(0, 2), 16)
  const g = parseInt(hex.substring(2, 4), 16)
  const b = parseInt(hex.substring(4, 6), 16)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b)
  return luminance > 186 ? '#212529' : '#fff'
}

const formatCurrencyWithFallback = (amount, currencyCode) => {
  return formatCurrency(amount, currencyCode || undefined)
}

const fetchPurchaseOrders = async () => {
  isLoading.value = true
  error.value = null
  
  try {
    const searchParams = {
      // DataGrid currentPage is 1-based; serviceFactory converts to API 0-based once.
      page: currentPage.value,
      pageSize: pageSize.value,
      searchText: searchText.value || '',
      sorting: 'creationTime desc'
    }
    
    // Apply server-side filtering by Vesproc status when a specific tab is active
    if (activeTab.value && activeTab.value !== 'all') {
      searchParams.vesprocStatusId = activeTab.value
    }
    
    const response = await purchaseOrderService.searchPurchaseOrders(searchParams)
    
    if (response && response.items) {
      purchaseOrders.value = response.items
      totalItems.value = response.totalCount || response.rowCount || 0

      // API returns 0-based currentPage; keep UI state 1-based.
      if (typeof response.currentPage === 'number') {
        currentPage.value = response.currentPage + 1
      }
      
      // Update status counts from API
      // API returns statusCounts with tab IDs as keys
      if (response.statusCounts) {
        statusCounts.value = { ...response.statusCounts }
      }
    }
  } catch (err) {
    console.error('Error fetching Sales Order:', err)
    error.value = t('purchaseOrder.errors.fetchFailed', 'Failed to load Sales Order. Please try again.')
  } finally {
    isLoading.value = false
  }
}

const viewPurchaseOrder = (item) => {
  router.push(`/purchase-order/${item.originalOrder?.id || item.id}`)
}

const editPurchaseOrder = (item) => {
  router.push(`/purchase-order/${item.originalOrder?.id || item.id}?edit=true`)
}

const deletePurchaseOrder = async (item) => {
  const order = item.originalOrder || item
  try {
    const result = await Swal.fire({
      title: t('common.confirmations.delete.title', 'Are you sure?'),
      text: t('purchaseOrder.confirmations.delete.message', 'This purchase order will be permanently deleted.'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: t('common.confirmations.delete.confirm', 'Yes, delete it!'),
      cancelButtonText: t('common.confirmations.delete.cancel', 'Cancel')
    })
    
    if (result.isConfirmed) {
      await purchaseOrderService.deletePurchaseOrder(order.id)
      
      store.dispatch('notify', {
        message: t('purchaseOrder.notifications.deleteSuccess', 'Purchase order deleted successfully'),
        type: 'success'
      })
      
      fetchPurchaseOrders()
    }
  } catch (err) {
    console.error('Error deleting purchase order:', err)
    store.dispatch('notify', {
      message: t('purchaseOrder.notifications.deleteFailed', 'Failed to delete purchase order'),
      type: 'error'
    })
  }
}

const getRenderActions = (item) => {
  const order = item.originalOrder || item
  const actions = ['view']
  if (canEditPurchaseOrders() && order.orderStatus === 'Pending') {
    actions.push('edit')
  }
  if (canDeletePurchaseOrders() && order.orderStatus === 'Pending') {
    actions.push('delete')
  }
  return actions
}

// Watchers
watch(activeTab, () => {
  currentPage.value = 1
  fetchPurchaseOrders()
})

// Lifecycle
onMounted(async () => {
  try {
    const defaults = await appStatusService.getVesprocDefaults()
    poVesprocStatuses.value = (defaults || []).filter(x => x.groupKey === 'VesprocPurchaseOrder')
  } catch (e) {
    console.error('Failed to load Vesproc defaults for PO tabs', e)
    poVesprocStatuses.value = []
  }
  fetchPurchaseOrders()
})
</script>

<style scoped>
.invoice-tab .nav-link {
  border: none;
  border-bottom: 2px solid transparent;
  border-radius: 0;
  color: #6c757d;
  font-weight: 500;
  padding: 12px 20px;
}

.invoice-tab .nav-link.active {
  border-bottom-color: var(--bs-primary);
  color: var(--bs-primary);
  background-color: transparent;
}

.invoice-tab .nav-link:hover {
  color: var(--bs-primary);
}

.po-status-badge {
  width: 24px;
  height: 24px;
  min-width: 24px;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  font-size: 11px !important;
  font-weight: 600;
  line-height: 1 !important;
  text-align: center;
  padding: 0 !important;
  vertical-align: middle;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>